import { useCallback, useEffect, useRef, useState } from 'react';
import { previewForSong, startPreview } from '../_helpers/cardSkin.js';

/**
 * "See what they see" (Skins, mockup 35, ENG-735): another listener's
 * now-playing card skin shown on this viewer's card. Local state only; no
 * one else's screen changes.
 *
 * `songKey` is the playing song (`playingSongKey`). `apply(card)` starts a
 * preview of the listener card's skin; `revert()` is "Back to mine". A
 * preview also ends when `songKey` changes. Returns `{ preview, apply,
 * revert }`, `preview` being `{ userId, name, look, songKey }` or null.
 */
export const useSkinPreview = (songKey) => {
  const [preview, setPreview] = useState(null);
  const songKeyRef = useRef(songKey ?? null);
  songKeyRef.current = songKey ?? null;

  const apply = useCallback((card) => {
    const next = startPreview(card, songKeyRef.current);
    setPreview(next);
    return Boolean(next);
  }, []);
  const revert = useCallback(() => setPreview(null), []);

  // Drop a preview left over from an earlier song, so it can't come back.
  useEffect(() => {
    setPreview((current) => previewForSong(current, songKey));
  }, [songKey]);

  // Checked at render too, so the old skin never draws for the new song.
  return { preview: previewForSong(preview, songKey), apply, revert };
};

export default useSkinPreview;
